import { useMemo, useState } from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ModelType } from "../../../../API";
import { EventViewModel } from "../../models/eventViewModel";
import { RecordingViewModel } from "../../models/recordingViewModel";
import { UpdateEventFeedbackInput } from "../../../../common/types/events/api";
import { useGetRecordingsByEvent } from "../../hooks/recordings/useGetRecordingsByEvent";
import { getEventStatusInfo } from "../../utils/eventStatusUtils";
import { RECORDING_PAGE_SIZE } from "../../utils/constants";
import { modelToServiceMap } from "../../../../common/variables/common";
import EventRecordingsTab from "./EventRecordingsTab";
import EventInformationTab from "./EventInformationTab";

interface EventModalContentProps {
  event: EventViewModel;
  newestRecording?: RecordingViewModel;
  onUpdateEventFeedback: (
    input: UpdateEventFeedbackInput,
    event: EventViewModel
  ) => Promise<void>;
  onInfoDirtyChange?: (dirty: boolean) => void;
}

enum EventTab {
  Recordings = 0,
  Information = 1,
}

const EventModalContent = ({
  event,
  newestRecording,
  onUpdateEventFeedback,
  onInfoDirtyChange,
}: EventModalContentProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<EventTab>(EventTab.Recordings);

  const handleTabChange = (_: unknown, newValue: EventTab) => {
    setActiveTab(newValue);
  };

  // Fetch recordings at modal level to calculate duration and pass down
  const { recordings, fetchNextPage, lastEvaluatedKey, loading } =
    useGetRecordingsByEvent({
      customerId: event.customerId.slice(2),
      eventTimestamp: event.timestamp,
      pageSize: RECORDING_PAGE_SIZE,
    });

  // Combine newestRecording (pre-fetched) with fetched recordings, avoiding duplicates
  const allRecordings = useMemo(() => {
    if (!newestRecording) return recordings;

    const filteredRecordings = recordings.filter(
      r => r.id !== newestRecording.id
    );

    return [newestRecording, ...filteredRecordings];
  }, [newestRecording, recordings]);

  // Determine if this is a leak event
  const isLeakEvent = event.modelId?.toLowerCase().includes("leak");

  // Get friendly event type name
  const eventTypeName =
    modelToServiceMap[event.modelId.toLowerCase() as ModelType] ||
    event.modelId;

  // Calculate duration from first to last recording
  const { duration, status } = useMemo(() => {
    return getEventStatusInfo(
      event.timestamp,
      newestRecording ? newestRecording.timestamp : "",
      allRecordings.length,
      event.modelId
    );
  }, [allRecordings, event.timestamp, newestRecording, event.modelId]);

  // Tab label based on event type
  const recordingsTabLabel = isLeakEvent ? "Recordings" : "Key Images";

  const tabContent = useMemo(() => {
    switch (activeTab) {
      case EventTab.Recordings:
        return (
          <EventRecordingsTab
            event={event}
            recordings={allRecordings}
            loading={loading}
            lastEvaluatedKey={lastEvaluatedKey}
            fetchNextPage={fetchNextPage}
          />
        );
      case EventTab.Information:
        return (
          <EventInformationTab
            event={event}
            newestRecording={newestRecording}
            onUpdateEventFeedback={onUpdateEventFeedback}
            onDirtyChange={onInfoDirtyChange}
          />
        );
      default:
        return null;
    }
  }, [
    activeTab,
    event,
    newestRecording,
    allRecordings,
    loading,
    lastEvaluatedKey,
    fetchNextPage,
    onUpdateEventFeedback,
  ]);

  return (
    <Box
      sx={{
        padding: "16px 24px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      {/* Header with event type and duration */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          {eventTypeName}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {duration && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                Duration: {duration}
              </Typography>
            </Box>
          )}
          <Typography variant="body2" color="text.secondary">
            Status: {status}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          borderBottom: theme => `1px solid ${theme.palette.divider}`,
          mb: 2,
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            minHeight: 40,
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              minHeight: 40,
              color: theme => theme.palette.text.secondary,
            },
            "& .Mui-selected": {
              color: theme => theme.palette.text.primary,
            },
            "& .MuiTabs-indicator": {
              height: 2,
            },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {recordingsTabLabel}
                {allRecordings.length > 0 && (
                  <Box
                    component="span"
                    sx={{
                      backgroundColor: theme => theme.palette.primary.main,
                      color: "white",
                      borderRadius: "10px",
                      px: 1,
                      py: 0.25,
                      fontSize: "0.75rem",
                      minWidth: 20,
                      textAlign: "center",
                    }}
                  >
                    {allRecordings.length}
                  </Box>
                )}
              </Box>
            }
          />
          <Tab label="Information" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", p: 1 }}>
        {tabContent}
      </Box>
    </Box>
  );
};

export default EventModalContent;
